import styled from 'styled-components'
import { Card } from '../../../components/shared/StyledComponents'

// =============================================================================
// Shared styled components for the LinkedIn feature.
// Strict theme tokens only; professional card look with a hover lift.
// =============================================================================

export const PageTitle = styled.h1`
  margin: 0 0 ${({ theme }) => theme.spacing.xs} 0;
  font-size: ${({ theme }) => theme.fontSizes.xl};
`

export const PageSubtitle = styled.p`
  margin: 0 0 ${({ theme }) => theme.spacing.lg} 0;
  color: ${({ theme }) => theme.colors.textMuted};
  font-size: ${({ theme }) => theme.fontSizes.sm};
`

export const SectionTitle = styled.h2`
  font-size: ${({ theme }) => theme.fontSizes.md};
  font-weight: 600;
  margin: ${({ theme }) => theme.spacing.xl} 0 ${({ theme }) => theme.spacing.md} 0;
  padding-bottom: ${({ theme }) => theme.spacing.sm};
  border-bottom: 2px solid ${({ theme }) => theme.colors.secondary};
`

/** A feed card with the professional hover-lift treatment. */
export const FeedCard = styled(Card)`
  display: flex;
  flex-direction: column;
  transition: transform ${({ theme }) => theme.transitions.normal},
    box-shadow ${({ theme }) => theme.transitions.normal};

  &:hover {
    transform: translateY(-3px);
    box-shadow: ${({ theme }) => theme.shadows.lg};
  }
`

export const FeedCardBody = styled.div`
  padding: ${({ theme }) => theme.spacing.md};
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.sm};
  flex: 1;
`

export const FeedCardFooter = styled.div`
  padding: ${({ theme }) => theme.spacing.sm} ${({ theme }) => theme.spacing.md};
  border-top: 1px solid ${({ theme }) => theme.colors.border};
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${({ theme }) => theme.spacing.sm};
  flex-wrap: wrap;
`

export const CardTitle = styled.h3`
  margin: 0;
  font-size: ${({ theme }) => theme.fontSizes.md};
  color: ${({ theme }) => theme.colors.primary};
  word-break: break-word;
`

export const CardText = styled.p`
  margin: 0;
  font-size: ${({ theme }) => theme.fontSizes.sm};
  color: ${({ theme }) => theme.colors.text};
  line-height: 1.5;
`

/** Card body text cut to a fixed number of lines so that a 40-word summary and a
 *  600-word raw post produce cards of comparable height — the list is meant to
 *  be scanned, and the full text is one click away in the modal. */
export const ClampedText = styled(CardText)<{ $lines?: number }>`
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: ${({ $lines = 4 }) => $lines};
  overflow: hidden;
  white-space: pre-wrap;
  word-break: break-word;
`

export const MutedText = styled.span`
  font-size: ${({ theme }) => theme.fontSizes.xs};
  color: ${({ theme }) => theme.colors.textMuted};
`

/** A compact "last commit" meta line shown on the repo card. */
export const CommitLine = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.4rem;
  font-size: ${({ theme }) => theme.fontSizes.xs};
  color: ${({ theme }) => theme.colors.textMuted};
  border-top: 1px dashed ${({ theme }) => theme.colors.border};
  padding-top: ${({ theme }) => theme.spacing.sm};
`

export const Sha = styled.code`
  font-family: monospace;
  background: ${({ theme }) => theme.colors.secondary};
  padding: 0.05rem 0.35rem;
  border-radius: ${({ theme }) => theme.borderRadius.sm};
  color: ${({ theme }) => theme.colors.text};
`

export const BadgeRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${({ theme }) => theme.spacing.xs};
  align-items: center;
`

interface ChipProps {
    $bg?: string
    $color?: string
}

/** A small rounded label used for category / tag / metric badges. */
export const Chip = styled.span<ChipProps>`
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  padding: 0.2rem 0.55rem;
  border-radius: ${({ theme }) => theme.borderRadius.sm};
  font-size: ${({ theme }) => theme.fontSizes.xs};
  font-weight: 600;
  white-space: nowrap;
  background: ${({ $bg, theme }) => $bg || theme.colors.secondary};
  color: ${({ $color, theme }) => $color || theme.colors.text};
`

/** A chip that is also a control (opens a detail). Looks like a Chip, behaves
 *  like a button, and stops the click from reaching the card underneath. */
export const ChipButton = styled.button<ChipProps>`
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  padding: 0.2rem 0.55rem;
  border: 1px solid transparent;
  border-radius: ${({ theme }) => theme.borderRadius.sm};
  font-size: ${({ theme }) => theme.fontSizes.xs};
  font-weight: 600;
  font-family: inherit;
  white-space: nowrap;
  cursor: pointer;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  background: ${({ $bg, theme }) => $bg || theme.colors.secondary};
  color: ${({ $color, theme }) => $color || theme.colors.text};

  &:hover {
    border-color: currentColor;
  }
`

export const ExternalLink = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  font-size: ${({ theme }) => theme.fontSizes.sm};
  font-weight: 600;
  color: ${({ theme }) => theme.colors.accent};
  text-decoration: none;

  &:hover {
    text-decoration: underline;
  }
`

/** Responsive grid for feed cards. */
export const CardGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
  gap: ${({ theme }) => theme.spacing.md};
`

// Inline alert styled like AdminPage's Alert (success / error / info)
export const Alert = styled.div<{ $type: 'success' | 'error' | 'info' }>`
  padding: ${({ theme }) => theme.spacing.md};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  margin-bottom: ${({ theme }) => theme.spacing.md};
  font-size: ${({ theme }) => theme.fontSizes.sm};

  ${({ $type }) => {
        switch ($type) {
            case 'success':
                return 'background: #e8f5e9; color: #2e7d32; border: 1px solid #a5d6a7;'
            case 'error':
                return 'background: #ffebee; color: #c62828; border: 1px solid #ef9a9a;'
            default:
                return 'background: #e3f2fd; color: #1565c0; border: 1px solid #90caf9;'
        }
    }}
`

// Color helpers for badges -----------------------------------------------------

/** Deterministic pastel background for a category/tag string. */
export const tagPalette = [
    { bg: '#E8F5E9', color: '#2E7D32' },
    { bg: '#E3F2FD', color: '#1565C0' },
    { bg: '#FFF3E0', color: '#EF6C00' },
    { bg: '#F3E5F5', color: '#7B1FA2' },
    { bg: '#FCE4EC', color: '#C2185B' },
    { bg: '#E0F2F1', color: '#00796B' },
]

export const paletteFor = (value: string) => {
    let hash = 0
    for (let i = 0; i < value.length; i++) {
        hash = (hash * 31 + value.charCodeAt(i)) >>> 0
    }
    return tagPalette[hash % tagPalette.length]
}

/** Green / amber / red for a 0-100 repo health score. Shared by the card chips
 *  and the detail modal so the same number is never two different colours. */
export const healthPalette = (score: number): { bg: string; color: string } => {
    if (score >= 75) return { bg: '#E8F5E9', color: '#2E7D32' }
    if (score >= 50) return { bg: '#FFF3E0', color: '#EF6C00' }
    return { bg: '#FFEBEE', color: '#C62828' }
}

/** Repo display label = owner/name (there is no full_name column on the backend). */
export const repoLabel = (repo: { owner: string; name: string }): string => `${repo.owner}/${repo.name}`

/** Where each platform actually lives.
 *
 *  No repo response carries a URL — there is no such column — so every link to a
 *  repo is built from (platform, owner, name). `platform` is derived backend-side
 *  as the first label of the host, which makes it 'bitbucket' for bitbucket.ORG:
 *  appending '.com' to it produced a link to a domain that is not the repo's, and
 *  for two of the three hosts we parse. */
const REPO_HOSTS: Record<string, string> = {
    github: 'github.com',
    gitlab: 'gitlab.com',
    bitbucket: 'bitbucket.org',
}

/** "Ver repositorio ↗" is the primary way out of the app for a repo, so an
 *  unknown platform falls back to GitHub rather than to a broken link: it is
 *  where all but a handful of these live. */
export const repoHref = (repo: { platform?: string | null; owner: string; name: string }): string => {
    const host = REPO_HOSTS[(repo.platform || '').toLowerCase()] || REPO_HOSTS.github
    return `https://${host}/${repo.owner}/${repo.name}`
}

/** Whether GitHub no longer serves this repo — deleted, or made private.
 *
 *  The repo list hides these; a post's own repos cannot be hidden, because they
 *  are the reason the post is in the feed. So they are shown, saying what they
 *  are, instead of being rendered as a live project with a link to a 404. */
export const repoIsGone = (repo: { is_active?: boolean | null; sync_status?: string | null }): boolean =>
    repo.is_active === false || repo.sync_status === 'gone'

/** How a repo or paper came to be attached to a post, as a chip suffix and a
 *  tooltip — or null when it was the author's own URL in the post body, which is
 *  the unqualified case and needs no words.
 *
 *  A guess and a comment we attributed must not look like something the author
 *  wrote out. The backend keeps the three apart from the database upward; this is
 *  the last step, and the only one a human ever sees. */
export const mentionQualifier = (
    mentionType?: string | null
): { suffix: string; hint: string } | null => {
    if (mentionType === 'name_search') {
        return {
            suffix: 'deducido',
            hint: 'Deducido del nombre del proyecto escrito en la publicación, no de un enlace',
        }
    }
    if (mentionType === 'comment_link') {
        return {
            suffix: 'en comentarios',
            hint: 'El autor escribió el enlace en su propio comentario, no en la publicación',
        }
    }
    return null
}

/** 1234 → "1,2 k". Star counts are a scanning cue, not an accounting figure, and
 *  five digits inside a chip push everything else off the row. */
export const compactCount = (n: number): string =>
    n >= 1000 ? `${(n / 1000).toFixed(1).replace('.0', '').replace('.', ',')} k` : String(n)

export const ESTADO_LABELS: Record<string, string> = {
    asignada: 'Asignada',
    en_progreso: 'En progreso',
    completada: 'Completada',
    cancelada: 'Cancelada',
}

export const ESTADO_COLORS: Record<string, { bg: string; color: string }> = {
    asignada: { bg: '#E3F2FD', color: '#1565C0' },
    en_progreso: { bg: '#FFF3E0', color: '#EF6C00' },
    completada: { bg: '#E8F5E9', color: '#2E7D32' },
    cancelada: { bg: '#FFEBEE', color: '#C62828' },
}
